import * as React from 'react';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { Modal, ModalContent as ModalContentBase, Button as ButtonV2 } from '@restworld/ui-ds-base';
import { cn, paymentProviderLogoMap } from '@restworld/utils-common';

interface PaymentMethod {
  id: string;
  customer: string;
  type?: string;
  brand?: string;
  last4?: string;
  expiry_month?: number;
  expiry_year?: number;
  payer_email?: string | null;
}

interface DetachPaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  paymentMethod: PaymentMethod | null;
  isDetaching?: boolean;
}

const ModalContentInner = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-10 items-center w-full', className)} {...props} />
);

const TextContent = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-2.5 items-start text-center w-full', className)} {...props} />
);

const Title = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h2 className={cn('text-[20px] font-semibold text-[#1c1c33] w-full leading-[1.5]', className)} {...props} />
);

const Description = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p className={cn('text-[16px] font-normal text-[#1c1c33] w-full leading-[1.5]', className)} {...props} />
);

const PaymentMethodSection = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'bg-[#fafafa] border border-[#dedee1] rounded-lg p-3 flex items-center justify-between w-full',
      className
    )}
    {...props}
  />
);

const PaymentMethodInfo = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex gap-8 items-center flex-1', className)} {...props} />
);

const PaymentMethodDetails = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-1 items-start justify-center', className)} {...props} />
);

const CardBrandText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p className={cn('text-[14px] font-medium text-[#1c1c33] leading-normal', className)} {...props} />
);

const ExpiryText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p className={cn('text-[14px] font-medium text-[#1c1c33] leading-normal', className)} {...props} />
);

const ButtonsContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-2.5 w-full', className)} {...props} />
);

export const DetachPaymentMethodModal: React.FC<DetachPaymentMethodModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  paymentMethod,
  isDetaching = false,
}) => {
  const { t } = useTranslation('jp-onboarding');

  if (!paymentMethod) return null;

  const isPayPal = paymentMethod.type === 'paypal';
  const paymentBrand = isPayPal ? 'paypal' : paymentMethod.brand?.toLowerCase();
  const paymentLogoSrc = paymentBrand && paymentProviderLogoMap[paymentBrand];
  const hasExpiry = !isPayPal && paymentMethod.expiry_month && paymentMethod.expiry_year;

  const footer = (
    <ButtonsContainer>
      <ButtonV2
        variant="secondary-danger"
        onClick={onConfirm}
        disabled={isDetaching}
        loading={isDetaching}
        className="h-12"
      >
        {t('steps.show_payment_methods.delete_payment_method')}
      </ButtonV2>
      <ButtonV2 variant="tertiary" onClick={onClose} disabled={isDetaching} className="h-12">
        {t('steps.show_payment_methods.cancel')}
      </ButtonV2>
    </ButtonsContainer>
  );

  return (
    <Modal open={isOpen} onOpenChange={(v) => !v && onClose()}>
      <ModalContentBase footer={footer} showCloseIcon>
        <ModalContentInner>
          <TextContent>
            <Title>{t('steps.show_payment_methods.delete_modal_title')}</Title>
            <Description>{t('steps.show_payment_methods.delete_modal_description')}</Description>
          </TextContent>

          <PaymentMethodSection>
            <PaymentMethodInfo>
              <PaymentMethodDetails>
                {paymentLogoSrc && (
                  <Image
                    src={paymentLogoSrc}
                    alt={paymentBrand || ''}
                    width={23}
                    height={16}
                    className="object-contain"
                  />
                )}
                <CardBrandText>
                  {isPayPal
                    ? paymentMethod.payer_email
                      ? `PayPal · ${paymentMethod.payer_email}`
                      : 'PayPal'
                    : `${paymentMethod.brand ?? ''} ***${paymentMethod.last4 ?? '****'}`}
                </CardBrandText>
              </PaymentMethodDetails>
              {hasExpiry && (
                <ExpiryText>
                  {t('steps.show_payment_methods.expiration')}:{' '}
                  {`${String(paymentMethod.expiry_month).padStart(2, '0')}/${String(paymentMethod.expiry_year).slice(
                    -2
                  )}`}
                </ExpiryText>
              )}
            </PaymentMethodInfo>
          </PaymentMethodSection>
        </ModalContentInner>
      </ModalContentBase>
    </Modal>
  );
};

export default DetachPaymentMethodModal;
